import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Clock, Users, FileImage, TrendingUp, Calendar, Smartphone, Search, Eye, Video as VideoIcon } from 'lucide-react';

interface MediaAnalyticsSectionProps {
  loading: boolean;
  data: any;
}

const MediaAnalyticsSection: React.FC<MediaAnalyticsSectionProps> = ({ loading, data }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMedia, setSelectedMedia] = useState<any>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // Função para formatar tempo em horas e segundos
  const formatTime = (seconds: number): string => {
    if (seconds < 60) {
      return `${seconds}s`;
    }

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${remainingSeconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      return `${remainingSeconds}s`;
    }
  };

  // Função para detectar o tipo real de mídia pela URL
  const getMediaType = (media: any): string => {
    const url = (media.url || '').toLowerCase();
    const type = (media.type || '').toLowerCase();

    // YouTube detection
    if (type === 'youtube' || url.includes('youtube.com') || url.includes('youtu.be')) {
      return 'youtube';
    }

    // GIF detection
    if (type === 'gif' || url.endsWith('.gif')) {
      return 'gif';
    }

    // Video detection
    if (type === 'video' || url.endsWith('.mp4') || url.endsWith('.webm') || url.endsWith('.mov') || url.endsWith('.avi')) {
      return 'video';
    }

    // Image detection
    if (type === 'image' || url.endsWith('.jpg') || url.endsWith('.jpeg') || url.endsWith('.png') || url.endsWith('.webp') || url.endsWith('.svg')) {
      return 'image';
    }

    // Se não detectou, retorna o tipo original ou 'link'
    return type || 'link';
  };

  const handlePreviewMedia = (media: any) => {
    setSelectedMedia(media);
    setIsPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setIsPreviewOpen(false);
    setSelectedMedia(null);
  };


  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-32 bg-gray-200 rounded animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  const { mediaAnalytics, mediaStats, allMediasWithClients } = data || {};

  // Debug logs
  console.log('🔍 Debug - allMediasWithClients:', allMediasWithClients);
  console.log('🔍 Debug - searchTerm:', searchTerm);
  console.log('🔍 Debug - Total mídias:', allMediasWithClients?.length || 0);

  // Filtragem de mídias
  const filteredMedias = allMediasWithClients?.filter((media: any) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const matchName = media.clientName?.toLowerCase().includes(searchLower);
    const matchEmail = media.clientEmail?.toLowerCase().includes(searchLower);
    return matchName || matchEmail;
  }) || [];

  console.log('🔍 Debug - Mídias filtradas:', filteredMedias.length);

  // Log dos nomes de clientes disponíveis
  if (allMediasWithClients && allMediasWithClients.length > 0) {
    const uniqueClients = [...new Set(allMediasWithClients.map((m: any) => m.clientName))];
    console.log('👥 Clientes disponíveis para busca:', uniqueClients);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <TrendingUp className="h-6 w-6 text-blue-600" />
        <h2 className="text-2xl font-bold">Análise de Mídias</h2>
      </div>

      {/* Estatísticas Gerais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Mídias</CardTitle>
            <FileImage className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{mediaStats?.active || 0}</div>
            <p className="text-xs text-muted-foreground">
              rodando nas telas
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tempo Médio</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatTime(mediaStats?.avgDisplayTime || 0)}</div>
            <p className="text-xs text-muted-foreground">
              por mídia
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Criadas (30d)</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{mediaStats?.recent || 0}</div>
            <p className="text-xs text-muted-foreground">
              últimas 4 semanas
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Análise por Cliente */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Análise por Cliente
          </CardTitle>
          <CardDescription>
            Detalhamento de mídias criadas por cada cliente
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cliente</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Total Mídias</TableHead>
                <TableHead>Mídias Ativas</TableHead>
                <TableHead>Tempo Médio</TableHead>
                <TableHead>Aparelhos</TableHead>
                <TableHead>Última Criação</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mediaAnalytics?.map((client: any, index: number) => (
                <TableRow key={index}>
                  <TableCell className="font-medium">{client.name}</TableCell>
                  <TableCell>{client.email}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{client.totalMedias}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={client.activeMedias > 0 ? "default" : "secondary"}>
                      {client.activeMedias}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatTime(client.avgDisplayTime)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Smartphone className="h-4 w-4 text-gray-500" />
                      <Badge variant={client.activeDevices > 0 ? "default" : "secondary"} className="text-xs">
                        {client.activeDevices || 0} aparelhos
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell>
                    {client.lastCreated
                      ? new Date(client.lastCreated).toLocaleDateString('pt-BR')
                      : 'N/A'
                    }
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Mídias Clientes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileImage className="h-5 w-5" />
            Mídias Clientes
          </CardTitle>
          <CardDescription>
            Visualize e busque mídias criadas por cada cliente
          </CardDescription>

          {/* Search Box */}
          <div className="relative mt-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Buscar por nome do cliente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Título</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Tempo</TableHead>
                <TableHead>Criada em</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredMedias.map((media: any) => (
                <TableRow key={media.id}>
                  <TableCell className="font-medium">{media.title}</TableCell>
                  <TableCell>{media.clientName}</TableCell>
                  <TableCell className="text-sm text-gray-600">{media.clientEmail}</TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {media.type === 'image' ? 'Imagem' :
                        media.type === 'video' ? 'Vídeo' :
                          media.type === 'youtube' ? 'YouTube' :
                            media.type === 'gif' ? 'GIF' : 'Outro'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={media.active ? 'default' : 'secondary'}>
                      {media.active ? 'Ativa' : 'Inativa'}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    {formatTime(media.display_time || 0)}
                  </TableCell>
                  <TableCell className="text-sm">
                    {new Date(media.created_at).toLocaleDateString('pt-BR')}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handlePreviewMedia(media)}
                      className="h-8 px-2"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}

              {filteredMedias.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-gray-500 py-8">
                    {searchTerm
                      ? `Nenhuma mídia encontrada para "${searchTerm}"`
                      : 'Nenhuma mídia disponível'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Modal de Preview de Mídia */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{selectedMedia?.title || 'Preview de Mídia'}</DialogTitle>
            <DialogDescription>
              Cliente: {selectedMedia?.clientName} ({selectedMedia?.clientEmail})
            </DialogDescription>
          </DialogHeader>

          <div className="flex justify-center items-center bg-black rounded-lg overflow-hidden min-h-[300px]">
            {selectedMedia && (() => {
              const detectedType = getMediaType(selectedMedia);

              // Imagens e GIFs
              if (detectedType === 'image' || detectedType === 'gif') {
                return (
                  <img
                    src={selectedMedia.url}
                    alt={selectedMedia.title}
                    className="max-w-full max-h-[65vh] object-contain"
                  />
                );
              }

              // Vídeos
              if (detectedType === 'video') {
                return (
                  <video
                    src={selectedMedia.url}
                    controls
                    className="max-w-full max-h-[65vh]"
                    autoPlay
                  />
                );
              }

              // YouTube
              if (detectedType === 'youtube') {
                const youtubeUrl = selectedMedia.url.includes('embed')
                  ? selectedMedia.url
                  : selectedMedia.url.replace('watch?v=', 'embed/');
                return (
                  <div className="w-full aspect-video">
                    <iframe
                      src={youtubeUrl}
                      className="w-full h-full"
                      allowFullScreen
                      title={selectedMedia.title}
                    />
                  </div>
                );
              }

              // Fallback para links ou tipos desconhecidos
              return (
                <div className="flex flex-col items-center justify-center p-8 text-white">
                  <FileImage className="h-16 w-16 mb-4 text-gray-400" />
                  <p className="text-lg mb-2">Preview não disponível</p>
                  <p className="text-sm text-gray-400 mb-4">Tipo: {selectedMedia.type}</p>
                  <a
                    href={selectedMedia.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded text-white"
                  >
                    Abrir mídia em nova aba
                  </a>
                </div>
              );
            })()}
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-semibold">Status:</span>{' '}
              <Badge variant={selectedMedia?.active ? 'default' : 'secondary'} className="ml-2">
                {selectedMedia?.active ? 'Ativa' : 'Inativa'}
              </Badge>
            </div>
            <div>
              <span className="font-semibold">Tempo de exibição:</span>{' '}
              {formatTime(selectedMedia?.display_time || 0)}
            </div>
            <div>
              <span className="font-semibold">Tipo:</span>{' '}
              <Badge variant="outline" className="ml-2">
                {(() => {
                  const detectedType = selectedMedia ? getMediaType(selectedMedia) : '';
                  return detectedType === 'image' ? 'Imagem' :
                    detectedType === 'video' ? 'Vídeo' :
                      detectedType === 'youtube' ? 'YouTube' :
                        detectedType === 'gif' ? 'GIF' :
                          detectedType === 'link' ? 'Link' : detectedType;
                })()}
              </Badge>
            </div>
            <div>
              <span className="font-semibold">Criada em:</span>{' '}
              {selectedMedia?.created_at && new Date(selectedMedia.created_at).toLocaleDateString('pt-BR')}
            </div>
          </div>

          <DialogFooter>
            <Button onClick={handleClosePreview}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default MediaAnalyticsSection; 